import {
  PrintableQuestion,
  fetchImageAsBase64,
  getMediaBasePath,
} from '@geneo2-web/shared-ui';
import { Box } from '@mui/material';
import {
  ContentElementType,
  ImageOption,
  ImageType,
  McqMultipleContentModel,
  McqSingleContentModel,
  OptionsType,
  Question,
  SolutionElementType,
} from '@protos/content_management/content.db_pb';
import { QuestionSectionMeta } from '@protos/learning_management/lms.db_pb';
import { useAppDispatch } from 'apps/teacher/src/reduxStore/reduxHooks';
import { renderElementToPdf } from '../../Common/PrintablePaper/pdfExportUtils';
import React, { CSSProperties, forwardRef, useRef, useState } from 'react';
import { setToastInfo } from '../../../Home/reducer/homeDashboard.slice';
import { getQuestionNumberFromIndex, numberToAlphabet } from './utils';
import { isGifMp4 } from 'libs/shared-ui/src/components/elements/ImageWrapper';
import { default as Geneologo } from 'libs/shared-ui/src/assets/images/geneo-logo.png';
import { generateDocxFromElement } from '../../Common/PrintablePaper/docxExportUtils';

const printableSx: CSSProperties = {
  breakInside: 'avoid',
  pageBreakInside: 'avoid',
};

interface IPropsPrintableQuestionPaper {
  instructionsHeading?: string;
  instructions?: string[];
  questionSectionMetaInfo: QuestionSectionMeta[];
  sectionMetaStatements: string[];
  questionsList?: Question[];
  printAnswer?: boolean;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  schoolName?: string;
  assessmentTitle?: string;
  grade?: string;
  section?: string;
  subject?: string;
  printableFontSize?: number;
  SchoolLogo?: string;
}

const PrintableQuestionPaper = forwardRef<any, IPropsPrintableQuestionPaper>(
  (
    {
      instructionsHeading,
      instructions,
      questionSectionMetaInfo,
      sectionMetaStatements,
      questionsList,
      printAnswer,
      loading,
      setLoading,
      schoolName,
      assessmentTitle,
      grade,
      section,
      subject,
      printableFontSize,
      SchoolLogo,
    },
    ref
  ) => {
    const printableRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch?.() || (() => {});
    const [showData, setShowData] = useState(false);
    // Table layout (kept together) for DOCX; flex layout (page-splittable) for PDF.
    const [useDocxLayout, setUseDocxLayout] = useState(false);
    const [fetchedQuestions, setFetchedQuestions] = useState<Question[]>(
      questionsList || []
    );
    const columnSx: CSSProperties = useDocxLayout
      ? {}
      : { display: 'flex', flexDirection: 'column' };
    const getLogoDimensions = (
      imgWidth: number,
      imgHeight: number,
      targetHeight: number
    ) => {
      const ratio = targetHeight / imgHeight;
      return {
        width: imgWidth * ratio,
        height: targetHeight,
      };
    };
    const loadImage = (src: string) =>
      new Promise<HTMLImageElement>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.src = src;
      });
    const processElements = async (
      arr: ContentElementType[] | SolutionElementType[]
    ) => {
      if (!Array.isArray(arr)) return arr;
      return Promise.all(
        arr.map(async (element) => {
          if (element?.model?.value instanceof ImageType) {
            const url = element.model.value.imageUrl;
            if (url && !url.startsWith('data:')) {
              let fullUrl = getMediaBasePath(url, 'processedMediaBucket');
              fullUrl = isGifMp4(fullUrl)
                ? fullUrl.replace('mp4', 'jpg')
                : fullUrl;
              const base64 = await fetchImageAsBase64(
                getMediaBasePath(fullUrl, 'processedMediaBucket')
              );
              if (base64) {
                element.model.value.imageUrl = base64;
              }
            }
          }
          return element;
        })
      );
    };

    const processOptions = async (arr: OptionsType[]) => {
      if (!Array.isArray(arr)) return arr;
      return Promise.all(
        arr.map(async (option) => {
          if (
            option?.optionType?.value instanceof ImageOption &&
            option.optionType.value.image instanceof ImageType
          ) {
            const url = option.optionType.value.image.imageUrl;
            if (url && !url.startsWith('data:')) {
              let fullUrl = getMediaBasePath(url, 'processedMediaBucket');
              fullUrl = isGifMp4(fullUrl)
                ? fullUrl.replace('mp4', 'jpg')
                : fullUrl;
              const base64 = await fetchImageAsBase64(
                getMediaBasePath(fullUrl, 'processedMediaBucket')
              );
              if (base64) {
                option.optionType.value.image.imageUrl = base64;
              }
            }
          }
          return option;
        })
      );
    };

    React.useImperativeHandle(ref, () => ({
      handlePrint: async (isAnswer = false, title = '', printasdoc = false) => {
        try {
          if (setLoading) setLoading(true);

          // --- Step 1: Preprocess Questions before Rendering ---
          const processedQuestions: Question[] = [];
          if (questionsList) {
            for (let i = 0; i < questionsList.length; i++) {
              const question = new Question(questionsList[i]);

              if (question.question?.model?.value?.commonQuestionContent) {
                // Elements
                question.question.model.value.commonQuestionContent.elements =
                  await processElements(
                    question.question.model.value.commonQuestionContent.elements
                  );

                // Solutions
                question.question.model.value.commonQuestionContent.solution =
                  await processElements(
                    question.question.model.value.commonQuestionContent.solution
                  );

                // Options (for MCQ)
                if (
                  question.question.model.value instanceof
                    McqSingleContentModel ||
                  question.question.model.value instanceof
                    McqMultipleContentModel
                ) {
                  question.question.model.value.options = await processOptions(
                    question.question.model.value.options
                  );
                }
              }

              processedQuestions.push(question);
            }
          }

          // --- Step 2: Render DOM with Processed Data ---
          setFetchedQuestions(processedQuestions);
          setUseDocxLayout(printasdoc);
          setShowData(true);

          // Allow React render & paint to complete
          await new Promise((r) => setTimeout(r, 300));

          const el = printableRef.current;
          if (!el) throw new Error('Printable element not found');

          // --- Step 3: Wait for all images to load ---
          const waitForImagesToLoad = async (container: HTMLElement) => {
            const imgs = Array.from(container.querySelectorAll('img'));
            await Promise.all(
              imgs.map(
                (img) =>
                  new Promise<void>((resolve) => {
                    if (img.complete) return resolve();
                    img.onload = img.onerror = () => resolve();
                  })
              )
            );
          };

          await waitForImagesToLoad(el);

          // --- Step 4: Wait for DOM layout stability ---
          await new Promise((r) => requestAnimationFrame(r));

          const fileBaseName = `${title || 'Assessment'}${
            isAnswer ? '-Answers' : '-Questions'
          }`;

          if (printasdoc) {
            // --- Step 5a: Generate DOCX ---
            await generateDocxFromElement({
              el,
              fontSizePx: printableFontSize,
              title: title || assessmentTitle || 'Assessment',
              filename: `${fileBaseName}.docx`,
            });
          } else {
            // --- Step 5b: Generate PDF ---
            const opt = {
              margin: [12, 12, 25, 12],
              filename: `${fileBaseName}.pdf`,
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: {
                scale: 2,
                useCORS: true,
              },
              jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
              pagebreak: {
                mode: ['css', 'legacy', 'whiteline'],
                avoid: ['.avoid-break-element', 'img'],
              },
            };

            const pdf = await renderElementToPdf(el, opt);
            const totalPages = pdf.internal.getNumberOfPages();
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const footerY = pageHeight - 8;
            const lineY = footerY - 8;

            const rightLogo = Geneologo;
            const leftLogo = SchoolLogo
              ? await fetchImageAsBase64(SchoolLogo)
              : undefined;
            const logoY = 5;

            // --- Step 6: Add Footer ---
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              pdf.setFontSize(10);
              pdf.setDrawColor(150);
              pdf.setLineWidth(0.2);
              pdf.line(10, lineY, pageWidth - 10, lineY);

              if (i === 1) {
                if (leftLogo) {
                  const leftLogoHeight = 15; // Desired logo height in mm
                  const leftImg = await loadImage(leftLogo);
                  const leftLogoSize = getLogoDimensions(
                    leftImg.width,
                    leftImg.height,
                    leftLogoHeight
                  );
                  pdf.addImage(
                    leftLogo,
                    'PNG',
                    10,
                    logoY,
                    leftLogoSize.width,
                    leftLogoSize.height
                  );

                  const rightLogoHeight = 10; // Desired logo height in mm
                  // RIGHT LOGO
                  const rightImg = await loadImage(rightLogo);
                  const rightLogoSize = getLogoDimensions(
                    rightImg.width,
                    rightImg.height,
                    rightLogoHeight
                  );
                  pdf.addImage(
                    rightLogo,
                    'PNG',
                    pageWidth - rightLogoSize.width - 10,
                    5,
                    rightLogoSize.width,
                    rightLogoSize.height
                  );
                }
                pdf.text('© Copyright reserved.', 10, footerY, {
                  align: 'left',
                });
                pdf.text(
                  `This Paper consists of ${totalPages} printed pages.`,
                  pageWidth / 2,
                  footerY,
                  { align: 'center' }
                );
              } else {
                pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, footerY, {
                  align: 'center',
                });
              }
            }

            await pdf.save(opt.filename);
          }

          // --- Step 7: Cleanup ---
          setShowData(false);
          setUseDocxLayout(false);
          setFetchedQuestions([]);
          if (setLoading) setLoading(false);
        } catch (error) {
          console.error('Error generating document:', error);
          dispatch(
            setToastInfo({
              label: printasdoc
                ? 'Error while generating doc'
                : 'Error while generating pdf',
              variant: 'error',
              open: true,
            })
          );
          setShowData(false);
          setUseDocxLayout(false);
          setFetchedQuestions([]);
          if (setLoading) setLoading(false);
        }
      },
    }));
    return (
      // Off-screen (not display:none) so KaTeX/fonts get real layout for capture
      <Box
        sx={{
          position: 'fixed',
          left: -10000,
          top: 0,
          width: 794,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        {showData && (
          <Box
            ref={printableRef}
            sx={{
              fontSize: printableFontSize
                ? printableFontSize + 'px !important'
                : undefined,
              backgroundColor: '#fff',
            }}
          >
            {schoolName && (
              <div
                style={{
                  color: '#000',
                  textAlign: 'center',
                  display: 'block',
                  padding: '5px 0',
                  fontWeight: 'bold',
                  ...printableSx,
                }}
                className="avoid-break-element"
              >
                {schoolName}
              </div>
            )}
            <div
              style={{
                ...columnSx,
                gap: useDocxLayout ? undefined : '2px',
                paddingBottom: '10px',
                ...printableSx,
              }}
              className="avoid-break-element"
            >
              {assessmentTitle && (
                <div
                  style={{
                    color: '#000',
                    textAlign: 'center',
                    display: 'block',
                    fontWeight: 'bold',
                    ...printableSx,
                  }}
                  className="avoid-break-element"
                >
                  {assessmentTitle}
                </div>
              )}
              {(subject || grade) && (
                <div
                  style={{
                    color: '#000',
                    textAlign: 'center',
                    display: 'block',
                    fontWeight: 'bold',
                    ...printableSx,
                  }}
                  className="avoid-break-element"
                >
                  {subject}{' '}
                  {grade ? `${subject ? ' - ' : ''}GRADE ${grade}` : ''}
                </div>
              )}
              {instructionsHeading && (
                <div
                  style={{
                    color: '#007CDC',
                    textAlign: 'center',
                    display: 'block',
                    ...printableSx,
                  }}
                  className="avoid-break-element"
                >
                  {instructionsHeading}
                </div>
              )}
            </div>
            {/* Instructions */}
            <div
              style={{
                ...columnSx,
                marginBottom: '5px',
                borderBottom: '1px solid #000',
                paddingBottom: '20px',
                ...printableSx,
              }}
              className="avoid-break-element"
            >
              {!!instructions &&
                instructions
                  .join('\n')
                  .split('\n')
                  .map((instruction, index) => (
                    <div
                      key={'instruction-' + index}
                      style={{ textAlign: 'left', ...printableSx }}
                      className="avoid-break-element"
                    >
                      {instruction}
                    </div>
                  ))}
            </div>
            {/* Question Sections */}
            {questionSectionMetaInfo.map((info, sectionIndex) => {
              const sortedSectionQuestionsInfo = info.sectionQuestionsInfo.sort(
                (a, b) => a.sequence - b.sequence
              );
              return (
                <React.Fragment key={'section-' + sectionIndex}>
                  {questionSectionMetaInfo.length > 1 && (
                    <div
                      style={{
                        color: '#007CDC',
                        textAlign: 'center',
                        display: 'block',
                        padding: '5px 0',
                        ...printableSx,
                      }}
                      className="avoid-break-element"
                    >
                      Section {numberToAlphabet(sectionIndex + 1)}{' '}
                      {info.questionSectionName
                        ? `- ${info.questionSectionName}`
                        : ''}
                    </div>
                  )}
                  {sectionMetaStatements[sectionIndex] &&
                    sectionMetaStatements[sectionIndex].length > 0 && (
                      <div
                        style={{
                          color: '#007CDC',
                          textAlign: 'center',
                          display: 'block',
                          padding: '5px 0',
                          ...printableSx,
                        }}
                        className="avoid-break-element"
                      >
                        {'(' + sectionMetaStatements[sectionIndex] + ')'}
                      </div>
                    )}
                  <div style={columnSx}>
                    {sortedSectionQuestionsInfo.map((ques, index) => {
                      const allQuestions = [
                        ques,
                        ...(ques.alternateQuestions
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((altQuestion) => altQuestion) || []),
                      ];
                      return (
                        <div
                          key={'sec_' + sectionIndex + '_ques_' + index}
                          style={columnSx}
                        >
                          {allQuestions.map((ques, altIndex) => {
                            const question = fetchedQuestions?.find(
                              (val) => val.questionId == ques.questionId
                            );
                            if (!question) return null;
                            const isAiGenerated = question?.isAiGenerated;
                            const showMedia = ques.showMedia;
                            return (
                              <React.Fragment
                                key={
                                  'sec_' +
                                  sectionIndex +
                                  '_ques_' +
                                  index +
                                  '_alt_' +
                                  altIndex
                                }
                              >
                                <PrintableQuestion
                                  ques={question}
                                  number={
                                    altIndex === 0
                                      ? getQuestionNumberFromIndex(
                                          sectionIndex,
                                          index,
                                          questionSectionMetaInfo
                                        )
                                      : undefined
                                  }
                                  marks={
                                    altIndex === 0
                                      ? question.question?.model.value
                                          ?.commonQuestionContent?.marks[0]
                                      : undefined
                                  }
                                  showAnswer={printAnswer}
                                  isAiGenerated={isAiGenerated}
                                  showMedia={showMedia}
                                  useTableLayout={useDocxLayout}
                                  splittable={!useDocxLayout}
                                />
                                {altIndex < allQuestions.length - 1 && (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      display: 'block',
                                      marginBottom: '5px',
                                      marginTop: '-5px',
                                      ...printableSx,
                                    }}
                                    className="avoid-break-element"
                                  >
                                    OR
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }
);

export default PrintableQuestionPaper;
